/**
 * Daily PnL tab on the SuperAdmin Platform page.
 *
 * Pick a date → see every user's realised P&L for that day with filters,
 * sort, row expansion (per-account drilldown + underlying/strategy/basket/
 * exit-reason breakdowns) and CSV export. Backed by GET /analytics/platform-pnl
 * which itself reads `analytics-daily` rows nightly-stamped by the aggregator
 * at 16:10 IST weekdays — so requests for today before ~4 PM IST naturally
 * return zero rows (banner explains this to the operator).
 *
 * Was the entire SuperAdminPlatformPnlPage prior to the tabs refactor.
 * Auth + page chrome now live on the host page; this component owns the
 * tab's own state and rendering.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  RefreshCw,
  Search,
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useActingAs } from '../../context/ActingAsContext';
import platformPnlService from '../../services/platformPnlService';
import { getHolidays } from '../../services/referenceDataService';
import { downloadFile } from '../../utils/downloadFile';
import { cn } from '../../utils/cn';
import { formatInr, formatPct, istCalendarToday, pnlClass } from '../../utils/platformFormat';
import type {
  PlatformPnlRow,
  PlatformPnlBreakdownEntry,
} from '../../types/analytics';

// ─── helpers ─────────────────────────────────────────────────────────────

const IST_OFFSET_MS = (5 * 60 + 30) * 60_000;

function isWeekend(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 || day === 6;
}

function isTradingDay(dateStr: string, holidays: Record<string, string>): boolean {
  return !isWeekend(dateStr) && !holidays[dateStr];
}

function previousTradingDay(from: string, holidays: Record<string, string>): string {
  const [y, m, d] = from.split('-').map(Number);
  const cursor = new Date(Date.UTC(y, m - 1, d));
  for (let i = 0; i < 14; i++) {
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    const iso = cursor.toISOString().slice(0, 10);
    if (isTradingDay(iso, holidays)) return iso;
  }
  return from;
}

function isStaleForToday(date: string): boolean {
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MS);
  const istDate = ist.toISOString().slice(0, 10);
  const istHour = ist.getUTCHours();
  const istMinute = ist.getUTCMinutes();
  // Aggregator runs at 16:10 IST — today's row isn't stamped until then.
  const beforeAggregator = istHour < 16 || (istHour === 16 && istMinute < 10);
  return date === istDate && beforeAggregator;
}

// ─── table columns ───────────────────────────────────────────────────────

type SortKey =
  | 'user'
  | 'net_pnl'
  | 'lifetime_net_pnl'
  | 'roi_pct'
  | 'capital_deployed'
  | 'trades_total'
  | 'win_rate'
  | 'avg_win'
  | 'avg_loss'
  | 'best_trade'
  | 'worst_trade';

type SortDir = 'asc' | 'desc';

interface ColumnDef {
  key: SortKey;
  label: string;
  align?: 'left' | 'right';
  sortable: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: 'user', label: 'User', align: 'left', sortable: true },
  { key: 'net_pnl', label: 'Day P&L', align: 'right', sortable: true },
  { key: 'lifetime_net_pnl', label: 'Lifetime P&L', align: 'right', sortable: true },
  { key: 'roi_pct', label: 'ROI %', align: 'right', sortable: true },
  { key: 'capital_deployed', label: 'Capital', align: 'right', sortable: true },
  { key: 'trades_total', label: 'Trades', align: 'right', sortable: true },
  { key: 'win_rate', label: 'Win %', align: 'right', sortable: true },
  { key: 'avg_win', label: 'Avg Win', align: 'right', sortable: true },
  { key: 'avg_loss', label: 'Avg Loss', align: 'right', sortable: true },
  { key: 'best_trade', label: 'Best', align: 'right', sortable: true },
  { key: 'worst_trade', label: 'Worst', align: 'right', sortable: true },
];

// ─── filter state ────────────────────────────────────────────────────────

type PnlBucket = 'all' | 'profit' | 'loss' | 'breakeven';

interface FilterState {
  search: string;
  bucket: PnlBucket;
  brokers: Set<string>;
  accountManagers: Set<string>;
  clientGroups: Set<string>;
  hideZeroTrades: boolean;
}

const EMPTY_FILTERS: FilterState = {
  search: '',
  bucket: 'all',
  brokers: new Set(),
  accountManagers: new Set(),
  clientGroups: new Set(),
  hideZeroTrades: true,
};

// ─── tab ────────────────────────────────────────────────────────────────

interface DailyPnlTabProps {
  /** Slot rendered into the PageHeader actions row — gives the tab a place
   *  to register its Refresh + Export CSV buttons. */
  registerActions: (node: React.ReactNode) => void;
}

const DailyPnlTab: React.FC<DailyPnlTabProps> = ({ registerActions }) => {
  const { setActingAs } = useActingAs();
  const navigate = useNavigate();

  const [holidays, setHolidays] = useState<Record<string, string>>({});
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);
  const [date, setDate] = useState<string>(() =>
    previousTradingDay(istCalendarToday(), {}),
  );
  const [rows, setRows] = useState<PlatformPnlRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getHolidays()
      .then((res) => {
        if (cancelled) return;
        const map = res.holidays || {};
        setHolidays(map);
        setHolidaysLoaded(true);
        setDate((prev) => (isTradingDay(prev, map)
          ? prev
          : previousTradingDay(prev, map)));
      })
      .catch(() => {
        if (!cancelled) setHolidaysLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('net_pnl');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchPage = useCallback(async (targetDate: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await platformPnlService.getPlatformPnl(targetDate);
      setRows(resp.rows || []);
      setExpanded(new Set());
    } catch (err: any) {
      setError(err?.message || 'Failed to load platform PnL');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(date); }, [date, fetchPage]);

  const brokerOptions = useMemo(() => {
    const out = new Set<string>();
    for (const r of rows) for (const a of r.accounts) if (a.broker) out.add(a.broker);
    return Array.from(out).sort();
  }, [rows]);

  const amOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      const am = r.account_manager;
      if (am) map.set(am.user_id, am.full_name || am.email || am.user_id);
    }
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const groupOptions = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    for (const r of rows) {
      const g = r.client_group;
      if (g) map.set(g.group_id, { name: g.name, color: g.color });
    }
    return Array.from(map.entries())
      .map(([id, val]) => ({ id, ...val }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (needle) {
        const haystack = `${r.full_name} ${r.email} ${r.user_id} ${r.accounts.map((a) => a.client_id).join(' ')}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      if (filters.bucket === 'profit' && r.net_pnl <= 0) return false;
      if (filters.bucket === 'loss' && r.net_pnl >= 0) return false;
      if (filters.bucket === 'breakeven' && r.net_pnl !== 0) return false;
      if (filters.hideZeroTrades && r.trades_total === 0) return false;
      if (filters.brokers.size > 0) {
        if (!r.accounts.some((a) => filters.brokers.has(a.broker))) return false;
      }
      if (filters.accountManagers.size > 0) {
        const amId = r.account_manager?.user_id;
        if (!amId || !filters.accountManagers.has(amId)) return false;
      }
      if (filters.clientGroups.size > 0) {
        const gid = r.client_group?.group_id;
        if (!gid || !filters.clientGroups.has(gid)) return false;
      }
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      if (sortKey === 'user') {
        return (a.full_name || a.email || a.user_id)
          .localeCompare(b.full_name || b.email || b.user_id) * dir;
      }
      const av = (a[sortKey] as number | null) ?? Number.NEGATIVE_INFINITY;
      const bv = (b[sortKey] as number | null) ?? Number.NEGATIVE_INFINITY;
      if (av === bv) return 0;
      return av < bv ? -1 * dir : 1 * dir;
    });
    return out;
  }, [rows, filters, sortKey, sortDir]);

  const kpis = useMemo(() => {
    const sumPnl = visibleRows.reduce((s, r) => s + r.net_pnl, 0);
    const sumLifetime = visibleRows.reduce((s, r) => s + r.lifetime_net_pnl, 0);
    const sumCap = visibleRows.reduce((s, r) => s + r.capital_deployed, 0);
    const profitable = visibleRows.filter((r) => r.net_pnl > 0).length;
    const lossMaking = visibleRows.filter((r) => r.net_pnl < 0).length;
    const tradedUsers = visibleRows.filter((r) => r.trades_total > 0).length;
    const trades = visibleRows.reduce((s, r) => s + r.trades_total, 0);
    const won = visibleRows.reduce((s, r) => s + r.trades_won, 0);
    const lost = visibleRows.reduce((s, r) => s + r.trades_lost, 0);
    const decided = won + lost;
    return {
      sumPnl,
      sumLifetime,
      sumCap,
      roi: sumCap > 0 ? (sumPnl / sumCap) * 100 : null,
      profitable,
      lossMaking,
      tradedUsers,
      trades,
      winRate: decided > 0 ? (won / decided) * 100 : null,
    };
  }, [visibleRows]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'user' ? 'asc' : 'desc');
    }
  };

  const toggleExpand = (uid: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleSetFilter = (
    field: 'brokers' | 'accountManagers' | 'clientGroups',
    value: string,
  ) => {
    setFilters((f) => {
      const next = new Set(f[field]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...f, [field]: next };
    });
  };

  const onExportCsv = useCallback(() => {
    const header = [
      'user_id', 'full_name', 'email', 'account_manager',
      'client_group', 'brokers', 'day_pnl', 'lifetime_pnl', 'roi_pct',
      'capital_deployed', 'trades_total', 'trades_won', 'trades_lost',
      'win_rate', 'avg_win', 'avg_loss', 'best_trade', 'worst_trade',
      'profit_factor', 'top_underlying', 'top_strategy',
    ];
    const escape = (v: any): string => {
      let s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (s && /^[=+\-@]/.test(s)) s = `'${s}`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      header.join(','),
      ...visibleRows.map((r) => [
        r.user_id, r.full_name, r.email,
        r.account_manager?.full_name || r.account_manager?.email || '',
        r.client_group?.name || '',
        r.accounts.map((a) => `${a.broker}:${a.client_id}`).join('|'),
        r.net_pnl, r.lifetime_net_pnl, r.roi_pct ?? '', r.capital_deployed,
        r.trades_total, r.trades_won, r.trades_lost,
        r.win_rate, r.avg_win, r.avg_loss, r.best_trade, r.worst_trade,
        r.profit_factor ?? '',
        r.top_underlying?.name || '', r.top_strategy?.name || '',
      ].map(escape).join(',')),
    ];
    downloadFile(`platform-pnl-${date}.csv`, lines.join('\n'), 'text/csv;charset=utf-8');
  }, [date, visibleRows]);

  // Stash the latest handlers in a ref so the registered actions always read
  // fresh closures without forcing the useEffect below to re-register on
  // every keystroke. Without this, `onExportCsv` (which depends on
  // visibleRows) and the inline `() => fetchPage(date)` were new function
  // identities on every render, so the search box's filter keystroke
  // re-registered the actions every time.
  const actionHandlersRef = useRef({
    onRefresh: () => fetchPage(date),
    onExportCsv,
  });
  actionHandlersRef.current.onRefresh = () => fetchPage(date);
  actionHandlersRef.current.onExportCsv = onExportCsv;

  // Boolean derived state — drives only the disabled/spinner UI, so the dep
  // array can stay small and the useEffect skips re-firing when the
  // observable button state hasn't changed.
  const canExport = visibleRows.length > 0;

  // Publish the tab-specific actions to the host PageHeader.
  useEffect(() => {
    registerActions(
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => actionHandlersRef.current.onRefresh()}
          leftIcon={<RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => actionHandlersRef.current.onExportCsv()}
          leftIcon={<Download className="h-4 w-4" />}
          disabled={!canExport}
        >
          Export CSV
        </Button>
      </div>
    );
  }, [registerActions, isLoading, canExport]);

  const showStaleBanner = isStaleForToday(date);
  const nonTradingDayReason: string | null = holidaysLoaded
    ? (holidays[date]
      ? `Market holiday: ${holidays[date]}`
      : isWeekend(date) ? 'Weekend — markets are closed' : null)
    : null;

  return (
    <div className="space-y-4">
      {showStaleBanner && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              The analytics aggregator runs at 16:10 IST on weekdays. Data for
              today won't be available until then. Pick a previous trading day
              for now.
            </span>
          </div>
        </div>
      )}

      {nonTradingDayReason && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap text-yellow-800 dark:text-yellow-200">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">
                {nonTradingDayReason}. No analytics data will exist for this date.
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDate(previousTradingDay(date, holidays))}
            >
              Jump to previous trading day
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
        <Kpi label="Day P&L" value={formatInr(kpis.sumPnl)} tone={kpis.sumPnl >= 0 ? 'pos' : 'neg'} />
        <Kpi label="Lifetime P&L" value={formatInr(kpis.sumLifetime)} tone={kpis.sumLifetime >= 0 ? 'pos' : 'neg'} />
        <Kpi label="Capital" value={formatInr(kpis.sumCap)} tone="neutral" />
        <Kpi label="ROI %" value={formatPct(kpis.roi)} tone={(kpis.roi ?? 0) >= 0 ? 'pos' : 'neg'} />
        <Kpi label="Users Traded" value={String(kpis.tradedUsers)} tone="neutral" />
        <Kpi label="Profitable" value={String(kpis.profitable)} tone="pos" />
        <Kpi label="Loss-making" value={String(kpis.lossMaking)} tone="neg" />
        <Kpi label="Trades" value={String(kpis.trades)} tone="neutral" />
        <Kpi label="Win Rate" value={formatPct(kpis.winRate, 1)} tone="neutral" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Per-user P&L ({visibleRows.length} of {rows.length})</CardTitle>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-300">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={istCalendarToday()}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                placeholder="Search name, email, or client ID…"
                leftIcon={<Search className="h-4 w-4" />}
                size="sm"
                className="w-72"
              />

              <select
                value={filters.bucket}
                onChange={(e) => setFilters((f) => ({ ...f, bucket: e.target.value as PnlBucket }))}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
              >
                <option value="all">All P&L</option>
                <option value="profit">Profitable only</option>
                <option value="loss">Loss-making only</option>
                <option value="breakeven">Breakeven only</option>
              </select>

              <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={filters.hideZeroTrades}
                  onChange={(e) => setFilters((f) => ({ ...f, hideZeroTrades: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Hide users with 0 trades
              </label>

              {(filters.brokers.size > 0
                || filters.accountManagers.size > 0
                || filters.clientGroups.size > 0
                || filters.search
                || filters.bucket !== 'all'
                || !filters.hideZeroTrades) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
                  Clear filters
                </Button>
              )}
            </div>

            {brokerOptions.length > 0 && (
              <ChipMultiSelect
                label="Broker"
                options={brokerOptions.map((b) => ({ id: b, label: b.toUpperCase() }))}
                selected={filters.brokers}
                onToggle={(v) => toggleSetFilter('brokers', v)}
              />
            )}
            {amOptions.length > 0 && (
              <ChipMultiSelect
                label="Account Manager"
                options={amOptions}
                selected={filters.accountManagers}
                onToggle={(v) => toggleSetFilter('accountManagers', v)}
              />
            )}
            {groupOptions.length > 0 && (
              <ChipMultiSelect
                label="Client Group"
                options={groupOptions.map((g) => ({ id: g.id, label: g.name, dot: g.color || undefined }))}
                selected={filters.clientGroups}
                onToggle={(v) => toggleSetFilter('clientGroups', v)}
              />
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading…</span>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              {rows.length === 0
                ? 'No analytics rows for this date. The aggregator may not have run yet.'
                : 'No users match the current filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 w-8" />
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        className={cn(
                          'px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300',
                          col.align === 'right' ? 'text-right' : 'text-left',
                          col.sortable && 'cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100',
                        )}
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key && (
                            <ChevronDown
                              className={cn('h-3 w-3 transition-transform', sortDir === 'asc' && 'rotate-180')}
                            />
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {visibleRows.map((r) => {
                    const isExpanded = expanded.has(r.user_id);
                    return (
                      <React.Fragment key={r.user_id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-3 py-3 align-top">
                            <button
                              type="button"
                              onClick={() => toggleExpand(r.user_id)}
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              {isExpanded
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {r.full_name || '(unnamed)'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{r.email || r.user_id}</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {r.accounts.map((a) => (
                                <span
                                  key={`${a.broker}:${a.client_id}`}
                                  className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase"
                                >
                                  {a.broker}:{a.client_id}
                                </span>
                              ))}
                              {r.account_manager && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                  AM: {r.account_manager.full_name || r.account_manager.email}
                                </span>
                              )}
                              {r.client_group && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                  <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: r.client_group.color || '#9ca3af' }}
                                  />
                                  {r.client_group.name}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(r.net_pnl))}>
                            {formatInr(r.net_pnl)}
                          </td>
                          <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(r.lifetime_net_pnl))}>
                            {formatInr(r.lifetime_net_pnl)}
                          </td>
                          <td className={cn('px-4 py-3 text-right tabular-nums', pnlClass(r.roi_pct ?? 0))}>
                            {formatPct(r.roi_pct)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            {formatInr(r.capital_deployed)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            <span className="font-medium">{r.trades_total}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                              ({r.trades_won}W/{r.trades_lost}L)
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            {formatPct(r.win_rate, 1)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            {formatInr(r.avg_win)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            {formatInr(r.avg_loss)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-green-700 dark:text-green-300">
                            {formatInr(r.best_trade)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-red-700 dark:text-red-300">
                            {formatInr(r.worst_trade)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Plumb display label — SA acting-as targets
                                // from this dashboard aren't in the managed
                                // pool, so the header chip would otherwise
                                // show a raw UUID.
                                setActingAs(
                                  r.user_id,
                                  r.full_name || r.email || r.user_id,
                                );
                                navigate('/others/analytics');
                              }}
                              leftIcon={<Eye className="h-3.5 w-3.5" />}
                              title="View this user's analytics"
                            >
                              View as
                            </Button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50/60 dark:bg-gray-900/40">
                            <td />
                            <td colSpan={COLUMNS.length + 1} className="px-4 py-4">
                              <ExpandedRow row={r} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── child components ────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: string;
  tone: 'pos' | 'neg' | 'neutral';
}

const Kpi: React.FC<KpiProps> = ({ label, value, tone }) => {
  const toneClass = tone === 'pos'
    ? 'text-green-600 dark:text-green-400'
    : tone === 'neg'
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</div>
      <div className={cn('text-xl font-bold mt-1 tabular-nums', toneClass)}>{value}</div>
    </div>
  );
};

interface ChipMultiSelectProps {
  label: string;
  options: { id: string; label: string; dot?: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}

const ChipMultiSelect: React.FC<ChipMultiSelectProps> = ({ label, options, selected, onToggle }) => (
  <div className="flex items-start gap-2 flex-wrap">
    <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-1.5 shrink-0">
      {label}
    </span>
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.has(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors',
              active
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-200'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700',
            )}
          >
            {opt.dot && (
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: opt.dot }}
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

interface ExpandedRowProps {
  row: PlatformPnlRow;
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({ row }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
    <BreakdownPanel title="By Broker Account" items={row.accounts.map((a) => ({
      key: `${a.broker}:${a.client_id}`,
      name: `${a.broker.toUpperCase()} ${a.client_id}`,
      pnl: a.net_pnl,
      count: a.trades_total,
      detail: `Capital ${formatInr(a.capital_deployed)} · Auto-login ${a.auto_login_enabled ? 'ON' : 'OFF'}`,
    }))} />
    <BreakdownPanel title="By Underlying" items={breakdownToItems(row.underlying_breakdown)} />
    <BreakdownPanel title="By Strategy" items={breakdownToItems(row.strategy_breakdown)} />
    <BreakdownPanel title="By Basket" items={breakdownToItems(row.basket_breakdown)} />
    <BreakdownPanel title="By Exit Reason" items={breakdownToItems(row.exit_reason_breakdown)} />
    <BreakdownPanel title="By Entry Hour" items={breakdownToItems(row.hour_breakdown)} />
  </div>
);

function breakdownToItems(map: Record<string, PlatformPnlBreakdownEntry>) {
  return Object.entries(map || {})
    .map(([key, val]) => ({
      key,
      name: val.name || key,
      pnl: Number(val.pnl) || 0,
      count: Number(val.count) || 0,
    }))
    .sort((a, b) => b.pnl - a.pnl);
}

interface BreakdownPanelProps {
  title: string;
  items: { key: string; name: string; pnl: number; count: number; detail?: string }[];
}

const BreakdownPanel: React.FC<BreakdownPanelProps> = ({ title, items }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{title}</div>
    {items.length === 0 ? (
      <div className="text-xs text-gray-400 dark:text-gray-500">No data</div>
    ) : (
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.key} className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-200">
            <div className="min-w-0 mr-2">
              <div className="truncate">{item.name}</div>
              {item.detail && (
                <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">{item.detail}</div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className={cn('font-medium tabular-nums', pnlClass(item.pnl))}>{formatInr(item.pnl)}</div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">{item.count} trades</div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default DailyPnlTab;
